import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import ShareIcon from '@mui/icons-material/Share';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import { saveAs } from 'file-saver';
import { generateReceiptGroupPdf, ReceiptGroupPrintData } from 'src/helpers/receiptGroup-helper';
import { DataService } from 'src/services/data-service';
import UpdateReceiptGroupDialog from './UpdateReceiptGroupDialog';

interface ReceiptGroupDetail {
  id: string;
  active: boolean;
  refNumber: string;
  description: string;
  groupDate: string;
  receiptCount: number;
  totalAmount: number;
  receipts: Receipt[];
  tenant: Tenant;
  lease: Lease;
  installments: Installment[] | null;
}

interface Receipt {
  id: string;
  active: boolean;
  amount: number;
  currency: string;
  paidAt: string;
  method: string;
  reference: string;
  paymentType: string;
  receiptGroupId: string;
  receiptGroupName: string;
  installment: {
    id: string;
    active: boolean;
    issuedAt: string;
    dueDate: string;
    status: string;
    total: number;
    currency: string;
    fee: Fee;
    lease: any;
  };
}

interface Tenant {
  id: string;
  active: boolean;
  fullName: string;
  emails: string[];
  phoneNumbers: string[];
  identificationType: string;
  identificationNumber?: string;
  dateOfBirth?: string;
  nationality: string;
  files: any[];
}

interface Lease {
  id: string;
  active: boolean;
  startDate: string;
  endDate: string;
  status: string;
  leaseType: string;
  currency: string;
  refNumber: string;
  securityDeposit?: number;
  monthlyRent?: number;
  unit: Unit;
  tenant: {
    id: string;
    active: boolean;
    fullName: string;
  };
  fees: Fee[];
}

interface Unit {
  id: string;
  active: boolean;
  number: string;
  floor: number;
  rooms: number;
  bathrooms: number;
  areaM2: number;
  property: Property;
}

interface Property {
  id: string;
  active: boolean;
  code: string;
  contact?: string;
  propertyType?: any;
  project: Project;
}

interface Project {
  id: string;
  active: boolean;
  name: string;
  number?: string;
  street?: string;
  cityName: string;
  countryName: string;
}

interface Fee {
  id: string;
  active: boolean;
  amount: number;
  currency: string;
  billingCycle: string;
  type: string;
}

interface Payment {
  receiptId: string;
  receiptReference: string;
  receiptGroupId: string;
  receiptGroupReference: string;
  amountPaid: number;
  paidAt: string;
  paymentMethod: string;
  paymentType: string;
  currency: string;
}

interface Installment {
  id: string;
  active: boolean;
  issuedAt: string;
  dueDate: string;
  status: string;
  total: number;
  paidAmount: number;
  remainingAmount: number;
  currency: string;
  fee: Fee;
  lease: {
    id: string;
    active: boolean;
    startDate: string;
    endDate: string;
    status: string;
  } | null;
  payments: Payment[] | null;
}

const PageHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
  color: 'white',
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
  boxShadow: theme.shadows[4]
}));

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  boxShadow: theme.shadows[2],
  '&:hover': {
    boxShadow: theme.shadows[4]
  }
}));

const PaymentMethodColors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'info'> = {
  'Cash': 'success',
  'CreditCard': 'primary',
  'DebitCard': 'info',
  'BankTransfer': 'secondary',
  'Check': 'warning',
  'MobilePayment': 'primary',
  'Other': 'default'
};

const InstallmentStatusColors: Record<string, 'warning' | 'success' | 'error' | 'default' | 'info'> = {
  'New': 'info',
  'Paid': 'success',
  'Overdue': 'error',
  'Cancelled': 'default'
};

const ReceiptGroupDetail: React.FC = () => {
  const { refNumber } = useParams<{ refNumber: string }>();
  const navigate = useNavigate();
  const [receiptGroup, setReceiptGroup] = useState<ReceiptGroupDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);

  // PDF state
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [printMenuAnchor, setPrintMenuAnchor] = useState<null | HTMLElement>(null);

  // Fee type selection state
  const [feeTypeSelectorOpen, setFeeTypeSelectorOpen] = useState(false);
  const [availableFeeTypes, setAvailableFeeTypes] = useState<string[]>([]);
  const [selectedFeeTypes, setSelectedFeeTypes] = useState<string[]>([]);
  const [feeTypeAction, setFeeTypeAction] = useState<'print' | 'preview' | 'download' | null>(null);

  useEffect(() => {
    if (refNumber) {
      fetchReceiptGroupDetail();
    }
  }, [refNumber]);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const fetchReceiptGroupDetail = async (installmentsParam: string = 'related') => {
    setLoading(true);
    setError(null);
    try {
      const response = await DataService.get(`api/ReceiptGroup/print/${refNumber}?installments=${installmentsParam}`);

      if (response.ok) {
        const responseData = await response.json();
        let groupData: ReceiptGroupDetail;

        if (responseData.success && responseData.data) {
          groupData = responseData.data;
        } else if (responseData.id) {
          groupData = responseData;
        } else {
          throw new Error('Invalid response structure');
        }

        setReceiptGroup(groupData);

        // Extract fee types when receipt group is loaded
        if (installmentsParam === 'related' || installmentsParam === 'none') {
          const feeTypesSet = new Set<string>();
          groupData.receipts?.forEach((receipt) => {
            const feeType = receipt.installment?.fee?.type;
            if (feeType) {
              feeTypesSet.add(feeType);
            }
          });
          const feeTypes = Array.from(feeTypesSet);
          setAvailableFeeTypes(feeTypes);
          setSelectedFeeTypes(feeTypes); // Select all by default
        }
      } else {
        throw new Error(`Failed to fetch receipt: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error fetching receipt:', error);
      const errorMessage = error instanceof Error
        ? error.message
        : 'Failed to load receipt details. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentMethodColor = (method: string) => {
    return PaymentMethodColors[method] || 'default';
  };

  const getInstallmentStatusColor = (status: string) => {
    return InstallmentStatusColors[status] || 'default';
  };

  // PDF Generation Functions
  const fetchPdfData = async (installmentsParam: string, feeTypes?: string[]): Promise<ReceiptGroupPrintData> => {
    try {
      const response = await DataService.get(`api/ReceiptGroup/print/${refNumber}?installments=${installmentsParam}`);

      if (response.ok) {
        const responseData = await response.json();
        let groupData: ReceiptGroupDetail;

        if (responseData.success && responseData.data) {
          groupData = responseData.data;
        } else if (responseData.id) {
          groupData = responseData;
        } else {
          throw new Error('Invalid response structure');
        }

        // Filter receipts by fee types if provided
        let filteredReceipts = groupData.receipts;
        let filteredTotalAmount = groupData.totalAmount;

        if (feeTypes && feeTypes.length > 0 && installmentsParam === 'none') {
          filteredReceipts = groupData.receipts.filter((receipt) => {
            const receiptFeeType = receipt.installment?.fee?.type;
            return receiptFeeType && feeTypes.includes(receiptFeeType);
          });

          // Recalculate total amount for filtered receipts
          filteredTotalAmount = filteredReceipts.reduce((sum, receipt) => sum + receipt.amount, 0);
        }

        return {
          id: groupData.id,
          refNumber: groupData.refNumber,
          description: groupData.description,
          groupDate: groupData.groupDate,
          receiptCount: filteredReceipts.length,
          totalAmount: filteredTotalAmount,
          receipts: filteredReceipts,
          tenant: groupData.tenant,
          lease: groupData.lease,
          installments: groupData.installments,
          includeInstallments: installmentsParam !== 'none'
        };
      } else {
        throw new Error(`Failed to fetch receipt group: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error fetching receipt group for PDF:', error);
      throw error;
    }
  };

  const handlePrintMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPrintMenuAnchor(event.currentTarget);
  };

  const handlePrintMenuClose = () => {
    setPrintMenuAnchor(null);
  };

  const handlePrintWithInstallments = async () => {
    handlePrintMenuClose();
    setPdfGenerating(true);
    setError(null);

    try {
      const pdfData = await fetchPdfData('all');
      const { blobUrl } = await generateReceiptGroupPdf(pdfData);
      const win = window.open(blobUrl);
      if (!win) {
        throw new Error('Failed to open print window. Please check your popup blocker settings.');
      }
    } catch (error) {
      console.error('Error printing receipt group:', error);
      setError(error instanceof Error ? error.message : 'Failed to print receipt group. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const handlePrintWithoutInstallments = async () => {
    handlePrintMenuClose();
    setPdfGenerating(true);
    setError(null);

    try {
      const pdfData = await fetchPdfData('none');
      const { blobUrl } = await generateReceiptGroupPdf(pdfData);
      const win = window.open(blobUrl);
      if (!win) {
        throw new Error('Failed to open print window. Please check your popup blocker settings.');
      }
    } catch (error) {
      console.error('Error printing receipt:', error);
      setError(error instanceof Error ? error.message : 'Failed to print receipt. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const handlePreviewWithInstallments = async () => {
    handlePrintMenuClose();
    setPdfGenerating(true);
    setError(null);

    try {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }

      const pdfData = await fetchPdfData('all');
      const { blobUrl } = await generateReceiptGroupPdf(pdfData);
      setPdfUrl(blobUrl);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate preview. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const handlePreviewWithoutInstallments = async () => {
    handlePrintMenuClose();
    setPdfGenerating(true);
    setError(null);

    try {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }

      const pdfData = await fetchPdfData('none');
      const { blobUrl } = await generateReceiptGroupPdf(pdfData);
      setPdfUrl(blobUrl);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate preview. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleDownloadWithInstallments = async () => {
    setPdfGenerating(true);
    setError(null);

    try {
      const pdfData = await fetchPdfData('all');
      const { blob } = await generateReceiptGroupPdf(pdfData);
      const filename = `Receipt-${receiptGroup?.refNumber}-with-installments.pdf`;
      saveAs(blob, filename);
    } catch (error) {
      console.error('Error downloading receipt group:', error);
      setError(error instanceof Error ? error.message : 'Failed to download receipt. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleDownloadWithoutInstallments = async () => {
    setPdfGenerating(true);
    setError(null);

    try {
      const pdfData = await fetchPdfData('none');
      const { blob } = await generateReceiptGroupPdf(pdfData);
      const filename = `ReceiptGroup-${receiptGroup?.refNumber}.pdf`;
      saveAs(blob, filename);
    } catch (error) {
      console.error('Error downloading receipt:', error);
      setError(error instanceof Error ? error.message : 'Failed to download receipt. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleOpenInNewTab = async () => {
    if (!pdfUrl) return;
    window.open(pdfUrl, '_blank');
  };

  const handleWebShare = async () => {
    if (!receiptGroup) return;

    setPdfGenerating(true);
    setError(null);
    setShareDialogOpen(false);

    try {
      const pdfData = await fetchPdfData('all');
      const { blob } = await generateReceiptGroupPdf(pdfData);
      const filename = `Receipt-${receiptGroup.refNumber}.pdf`;
      const file = new File([blob], filename, { type: 'application/pdf' });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `Receipt Group - ${receiptGroup.refNumber}`,
          text: `Receipt group for ${receiptGroup.tenant.fullName} (Ref: ${receiptGroup.refNumber})`,
          files: [file]
        });
      } else {
        // Fallback: Download the file and show info
        saveAs(blob, filename);
        alert('File downloaded. You can now attach it to an email or share it manually.');
      }
    } catch (error) {
      console.error('Error sharing PDF:', error);
      setError(error instanceof Error ? error.message : 'Failed to share PDF. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleEmailReceipt = async () => {
    if (!receiptGroup) return;

    setPdfGenerating(true);
    setError(null);
    setShareDialogOpen(false);

    try {
      const pdfData = await fetchPdfData('all');
      const { blob } = await generateReceiptGroupPdf(pdfData);
      const filename = `Receipt-${receiptGroup.refNumber}.pdf`;

      // Create mailto link with tenant email
      const tenantEmail = receiptGroup.tenant.emails?.[0] || '';
      const subject = encodeURIComponent(`Receipt Group - ${receiptGroup.refNumber}`);
      const body = encodeURIComponent(
        `Dear ${receiptGroup.tenant.fullName},\n\nPlease find your receipt attached.\n\nReceipt Reference: ${receiptGroup.refNumber}\nDate: ${new Date(receiptGroup.groupDate).toLocaleDateString()}\nTotal Amount: ${receiptGroup.lease.currency} ${receiptGroup.totalAmount.toLocaleString()}\n\nBest regards`
      );
      const mailtoLink = `mailto:${tenantEmail}?subject=${subject}&body=${body}`;

      // Download the file and open mailto
      saveAs(blob, filename);
      window.location.href = mailtoLink;

      alert('The PDF has been downloaded. Please attach it to the email that will open.');
    } catch (error) {
      console.error('Error preparing email:', error);
      setError(error instanceof Error ? error.message : 'Failed to prepare email. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  // Fee type selection handlers
  const handleOpenFeeTypeSelector = (action: 'print' | 'preview' | 'download') => {
    handlePrintMenuClose();
    setFeeTypeAction(action);
    setFeeTypeSelectorOpen(true);
  };

  const handleCloseFeeTypeSelector = () => {
    setFeeTypeSelectorOpen(false);
    setFeeTypeAction(null);
  };

  const handleToggleFeeType = (feeType: string) => {
    setSelectedFeeTypes((prev) =>
      prev.includes(feeType)
        ? prev.filter((ft) => ft !== feeType)
        : [...prev, feeType]
    );
  };

  const handleSelectAllFeeTypes = () => {
    setSelectedFeeTypes(availableFeeTypes);
  };

  const handleDeselectAllFeeTypes = () => {
    setSelectedFeeTypes([]);
  };

  const handleConfirmFeeTypeSelection = async () => {
    if (selectedFeeTypes.length === 0) {
      setError('Please select at least one fee type.');
      return;
    }

    setFeeTypeSelectorOpen(false);

    // Execute the action with selected fee types
    if (feeTypeAction === 'print') {
      await handlePrintWithSelectedFeeTypes();
    } else if (feeTypeAction === 'preview') {
      await handlePreviewWithSelectedFeeTypes();
    } else if (feeTypeAction === 'download') {
      await handleDownloadWithSelectedFeeTypes();
    }
  };

  const handlePrintWithSelectedFeeTypes = async () => {
    setPdfGenerating(true);
    setError(null);

    try {
      const pdfData = await fetchPdfData('none', selectedFeeTypes);
      const { blobUrl } = await generateReceiptGroupPdf(pdfData);
      const win = window.open(blobUrl);
      if (!win) {
        throw new Error('Failed to open print window. Please check your popup blocker settings.');
      }
    } catch (error) {
      console.error('Error printing receipt:', error);
      setError(error instanceof Error ? error.message : 'Failed to print receipt. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const handlePreviewWithSelectedFeeTypes = async () => {
    setPdfGenerating(true);
    setError(null);

    try {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }

      const pdfData = await fetchPdfData('none', selectedFeeTypes);
      const { blobUrl } = await generateReceiptGroupPdf(pdfData);
      setPdfUrl(blobUrl);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate preview. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleDownloadWithSelectedFeeTypes = async () => {
    setPdfGenerating(true);
    setError(null);

    try {
      const pdfData = await fetchPdfData('none', selectedFeeTypes);
      const { blob } = await generateReceiptGroupPdf(pdfData);
      const filename = `ReceiptGroup-${receiptGroup?.refNumber}-selected-fees.pdf`;
      saveAs(blob, filename);
    } catch (error) {
      console.error('Error downloading receipt:', error);
      setError(error instanceof Error ? error.message : 'Failed to download receipt. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !receiptGroup) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Receipt not found'}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/receipts')}
        >
          Back to Receipts
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 1, px: 2 }}>
      {/* Page Header */}
      <PageHeader>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => navigate('/receipts')}
              size="small"
              sx={{
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)'
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography
                variant="h5"
                component="h1"
                fontWeight="bold"
                sx={{ mb: 0.5 }}
              >
                Receipt Details
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Ref: {receiptGroup.refNumber}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setUpdateDialogOpen(true)}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              Edit Receipts
            </Button>
            <Button
              variant="contained"
              startIcon={pdfGenerating ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={handlePrintMenuOpen}
              disabled={pdfGenerating}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              Print
            </Button>
            <Button
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={() => setShareDialogOpen(true)}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.4)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Share
            </Button>
          </Stack>
        </Box>
      </PageHeader>

      {/* Receipt Group Information */}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        {/* Group Details */}
        <Grid item xs={12} md={6}>
          <SectionCard>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
              >
                <ReceiptIcon color="primary" />
                Receipt Details
              </Typography>

              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Reference Number
                  </Typography>
                  <Typography variant="h6">{receiptGroup.refNumber}</Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Description
                  </Typography>
                  <Typography variant="body1">{receiptGroup.description || '-'}</Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Receipt Date
                  </Typography>
                  <Typography variant="body1">{formatDateTime(receiptGroup.groupDate)}</Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Total Amount
                  </Typography>
                  <Typography variant="h4" color="success.main" fontWeight="bold">
                    {receiptGroup.lease.currency} {receiptGroup.totalAmount.toLocaleString()}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Payment Count
                  </Typography>
                  <Chip
                    label={`${receiptGroup.receiptCount} receipts`}
                    color="info"
                    sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </SectionCard>
        </Grid>

        {/* Tenant & Lease Information */}
        <Grid item xs={12} md={6}>
          <SectionCard>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
              >
                <PersonIcon color="primary" />
                Tenant & Lease
              </Typography>

              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Tenant Name
                  </Typography>
                  <Typography variant="h6">
                    {receiptGroup.tenant.fullName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {receiptGroup.tenant.emails?.[0] || 'No email'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {receiptGroup.tenant.phoneNumbers?.[0] || 'No phone'}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Unit
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HomeIcon color="primary" />
                    <Typography variant="h6">
                      Unit {receiptGroup.lease.unit.number}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {receiptGroup.lease.unit.rooms} rooms • {receiptGroup.lease.unit.bathrooms} bathrooms • Floor {receiptGroup.lease.unit.floor}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Property
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {receiptGroup.lease.unit.property.code}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {receiptGroup.lease.unit.property.project.street || receiptGroup.lease.unit.property.project.cityName}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Lease Period
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(receiptGroup.lease.startDate)} - {formatDate(receiptGroup.lease.endDate)}
                  </Typography>
                  <Chip
                    label={receiptGroup.lease.status}
                    size="small"
                    color={receiptGroup.lease.status === 'Active' ? 'success' : 'default'}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Receipts Section */}
      <SectionCard sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 1.5 }}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1.5,
              fontWeight: 'bold'
            }}
          >
            <PaymentIcon color="primary" />
            Receipt Details ({receiptGroup.receipts.length})
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>Fee Type</strong></TableCell>
                  <TableCell><strong>Method</strong></TableCell>
                  <TableCell><strong>Payment Type</strong></TableCell>
                  <TableCell><strong>Amount</strong></TableCell>
                  {/* <TableCell><strong>Receipt References</strong></TableCell> */}
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  // Group receipts by method, payment type, currency, and fee type
                  const groupedReceipts = receiptGroup.receipts.reduce((acc, receipt) => {
                    const feeType = receipt.installment?.fee?.type || 'N/A';
                    const key = `${receipt.method}|${receipt.paymentType}|${receipt.currency}|${feeType}`;

                    if (!acc[key]) {
                      acc[key] = {
                        method: receipt.method,
                        paymentType: receipt.paymentType,
                        currency: receipt.currency,
                        feeType: feeType,
                        totalAmount: 0,
                        receipts: [],
                        dates: []
                      };
                    }

                    acc[key].totalAmount += receipt.amount;
                    acc[key].receipts.push(receipt.reference);
                    acc[key].dates.push(receipt.paidAt);

                    return acc;
                  }, {} as Record<string, {
                    method: string;
                    paymentType: string;
                    currency: string;
                    feeType: string;
                    totalAmount: number;
                    receipts: string[];
                    dates: string[];
                  }>);

                  return Object.values(groupedReceipts).map((group, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          {index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          {group.feeType}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={group.method}
                          size="small"
                          color={getPaymentMethodColor(group.method)}
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {group.paymentType || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <AttachMoneyIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          <Typography variant="body2" fontWeight="600" color="success.main">
                            {group.currency} {group.totalAmount.toLocaleString()}
                          </Typography>
                        </Stack>
                        {/* <Typography variant="caption" color="text.secondary" display="block">
                          ({group.receipts.length} receipt{group.receipts.length > 1 ? 's' : ''})
                        </Typography> */}
                      </TableCell>
                      {/* <TableCell>
                        <Box>
                          {group.receipts.map((ref, idx) => (
                            <Box key={idx} sx={{ mb: 0.5 }}>
                              <Typography variant="caption" fontWeight="600" color="primary">
                                {ref}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {formatDateTime(group.dates[idx])}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </TableCell> */}
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </SectionCard>

      {/* Installments Section */}
      {receiptGroup.installments && receiptGroup.installments.length > 0 && (
        <SectionCard>
          <CardContent sx={{ p: 1.5 }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1.5,
                fontWeight: 'bold'
              }}
            >
              <DescriptionIcon color="primary" />
              Installments ({receiptGroup.installments.length})
            </Typography>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>#</strong></TableCell>
                    <TableCell><strong>Issued Date</strong></TableCell>
                    <TableCell><strong>Due Date</strong></TableCell>
                    <TableCell><strong>Fee Type</strong></TableCell>
                    <TableCell><strong>Total Amount</strong></TableCell>
                    <TableCell><strong>Paid Amount</strong></TableCell>
                    <TableCell><strong>Remaining</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Payments</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receiptGroup.installments.map((installment, index) => {
                    // Group payments by receipt group
                    const paymentsByGroup = installment.payments?.reduce((acc, payment) => {
                      const groupRef = payment.receiptGroupReference;
                      if (!acc[groupRef]) {
                        acc[groupRef] = {
                          receiptGroupId: payment.receiptGroupId,
                          receiptGroupReference: groupRef,
                          payments: [],
                          totalAmount: 0,
                          latestPaymentDate: payment.paidAt
                        };
                      }
                      acc[groupRef].payments.push(payment);
                      acc[groupRef].totalAmount += payment.amountPaid;
                      if (new Date(payment.paidAt) > new Date(acc[groupRef].latestPaymentDate)) {
                        acc[groupRef].latestPaymentDate = payment.paidAt;
                      }
                      return acc;
                    }, {} as Record<string, { receiptGroupId: string; receiptGroupReference: string; payments: Payment[]; totalAmount: number; latestPaymentDate: string }>);

                    return (
                      <TableRow key={installment.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">
                            {index + 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(installment.issuedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(installment.dueDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {installment.fee.type}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {installment.fee.billingCycle}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">
                            {installment.currency} {installment.total.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600" color={installment.paidAmount > 0 ? "success.main" : "text.secondary"}>
                            {installment.currency} {installment.paidAmount.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600" color={installment.remainingAmount > 0 ? "warning.main" : "success.main"}>
                            {installment.currency} {installment.remainingAmount.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={installment.status}
                            size="small"
                            color={getInstallmentStatusColor(installment.status)}
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell>
                          {installment.payments && installment.payments.length > 0 ? (
                            <Box>
                              {paymentsByGroup && Object.values(paymentsByGroup).map((group, idx) => (
                                <Box key={idx} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                                  <Typography variant="caption" fontWeight="bold" color="primary">
                                    {group.receiptGroupReference}
                                  </Typography>
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    Paid: {formatDateTime(group.latestPaymentDate)}
                                  </Typography>
                                  <Typography variant="caption" display="block" fontWeight="600" color="success.main">
                                    Total: {installment.currency} {group.totalAmount.toLocaleString()}
                                  </Typography>
                                  {/* <Typography variant="caption" display="block" color="text.secondary">
                                    ({group.payments.length} receipt{group.payments.length > 1 ? 's' : ''})
                                  </Typography> */}
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No payments yet
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </SectionCard>
      )}

      {/* Print Menu */}
      <Menu
        anchorEl={printMenuAnchor}
        open={Boolean(printMenuAnchor)}
        onClose={handlePrintMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        <MenuItem onClick={handlePreviewWithInstallments}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText
            primary="Preview with Installments"
            secondary="View complete receipt group"
          />
        </MenuItem>
        <MenuItem onClick={handlePreviewWithoutInstallments}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText
            primary="Preview without Installments"
            secondary="View receipts only"
          />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handlePrintWithInstallments}>
          <ListItemIcon>
            <PrintIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText
            primary="Print with Installments"
            secondary="Include all installment details"
          />
        </MenuItem>
        <MenuItem onClick={handlePrintWithoutInstallments}>
          <ListItemIcon>
            <PrintIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText
            primary="Print without Installments"
            secondary="Receipts only"
          />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDownloadWithInstallments}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" color="success" />
          </ListItemIcon>
          <ListItemText
            primary="Download with Installments"
            secondary="Save PDF with full details"
          />
        </MenuItem>
        <MenuItem onClick={handleDownloadWithoutInstallments}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" color="success" />
          </ListItemIcon>
          <ListItemText
            primary="Download without Installments"
            secondary="Save PDF with receipts only"
          />
        </MenuItem>
        {availableFeeTypes.length > 1 && (
          <>
            <Divider sx={{ my: 1 }} />
            <MenuItem onClick={() => handleOpenFeeTypeSelector('preview')}>
              <ListItemIcon>
                <FilterListIcon fontSize="small" color="secondary" />
              </ListItemIcon>
              <ListItemText
                primary="Preview Selected Fee Types"
                secondary="Choose specific fees to preview"
              />
            </MenuItem>
            <MenuItem onClick={() => handleOpenFeeTypeSelector('print')}>
              <ListItemIcon>
                <FilterListIcon fontSize="small" color="secondary" />
              </ListItemIcon>
              <ListItemText
                primary="Print Selected Fee Types"
                secondary="Choose specific fees to print"
              />
            </MenuItem>
            <MenuItem onClick={() => handleOpenFeeTypeSelector('download')}>
              <ListItemIcon>
                <FilterListIcon fontSize="small" color="secondary" />
              </ListItemIcon>
              <ListItemText
                primary="Download Selected Fee Types"
                secondary="Choose specific fees to download"
              />
            </MenuItem>
          </>
        )}
      </Menu>

      {/* PDF Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Receipt Group Preview - {receiptGroup?.refNumber}
        </DialogTitle>
        <DialogContent dividers sx={{ height: '80vh', p: 0 }}>
          {pdfUrl ? (
            <iframe
              title="Receipt Group PDF Preview"
              src={pdfUrl}
              style={{ border: 'none', width: '100%', height: '100%' }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => setPreviewOpen(false)} variant="outlined">Close</Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleOpenInNewTab}
              variant="outlined"
              startIcon={<VisibilityIcon />}
            >
              Open in New Tab
            </Button>
            <Button
              onClick={handlePrintMenuOpen}
              variant="contained"
              startIcon={<PrintIcon />}
            >
              Print Options
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Update Receipt Group Dialog */}
      {receiptGroup && (
        <UpdateReceiptGroupDialog
          open={updateDialogOpen}
          onClose={() => setUpdateDialogOpen(false)}
          receiptGroup={receiptGroup}
          onSuccess={() => {
            fetchReceiptGroupDetail();
          }}
        />
      )}

      {/* Share Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShareIcon />
            Share Receipt Group
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={pdfGenerating ? <CircularProgress size={20} /> : <ShareIcon />}
                onClick={handleWebShare}
                disabled={pdfGenerating}
                sx={{ py: 2, justifyContent: 'flex-start' }}
              >
                <Box sx={{ flex: 1, textAlign: 'left' }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Share via Apps
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Share to WhatsApp, Messenger, etc.
                  </Typography>
                </Box>
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={pdfGenerating ? <CircularProgress size={20} /> : <EmailIcon />}
                onClick={handleEmailReceipt}
                disabled={pdfGenerating}
                sx={{ py: 2, justifyContent: 'flex-start' }}
              >
                <Box sx={{ flex: 1, textAlign: 'left' }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Share via Email
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Send to {receiptGroup?.tenant.emails?.[0] || 'tenant'}
                  </Typography>
                </Box>
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setShareDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fee Type Selector Dialog */}
      <Dialog open={feeTypeSelectorOpen} onClose={handleCloseFeeTypeSelector} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            bgcolor: 'secondary.main',
            color: 'white',
            fontWeight: 'bold',
            py: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon />
            Select Fee Types
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Select the fee types you want to include in your receipt. Only receipts without installments will be filtered.
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Button
              size="small"
              onClick={handleSelectAllFeeTypes}
              variant="outlined"
              color="secondary"
            >
              Select All
            </Button>
            <Button
              size="small"
              onClick={handleDeselectAllFeeTypes}
              variant="outlined"
              color="secondary"
            >
              Deselect All
            </Button>
          </Box>

          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <FormGroup>
              {availableFeeTypes.map((feeType) => (
                <FormControlLabel
                  key={feeType}
                  control={
                    <Checkbox
                      checked={selectedFeeTypes.includes(feeType)}
                      onChange={() => handleToggleFeeType(feeType)}
                      color="secondary"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight="500">
                      {feeType}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          </Paper>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            {selectedFeeTypes.length} of {availableFeeTypes.length} fee type(s) selected
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseFeeTypeSelector}
            variant="outlined"
            disabled={pdfGenerating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmFeeTypeSelection}
            variant="contained"
            color="secondary"
            disabled={pdfGenerating || selectedFeeTypes.length === 0}
            startIcon={pdfGenerating ? <CircularProgress size={20} /> : null}
          >
            {pdfGenerating ? 'Processing...' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReceiptGroupDetail;
